import type { SerializedError } from '@reduxjs/toolkit';
import { useMemo } from 'react';

import { useGetProfileEligibilityNgemrPatientQuery } from '@shared/services/profileEligibility';
import { useGetProfileBasicQuery, useGetProfilePatientMasterQuery, userProfileApi } from '@shared/services/userProfile';
import { PROFILE_BASIC_TAGS } from '@shared/services/userProfile/endpoints/getProfileBasic';
import { PROFILE_PATIENT_MASTER_TAGS } from '@shared/services/userProfile/endpoints/getProfilePatientMaster';
import type { GetProfileBasicPayload, ProfileBasic } from '@shared/services/userProfile/types/getProfileBasic.types';
import type { GetProfilePatientMasterPayload } from '@shared/services/userProfile/types/getProfilePatientMaster.types';
import { useAppDispatch } from '@shared/stores/rootTypes';
import type { ProfileFull } from '@shared/types/user.types';

import useIsAuthenticated from '../useIsAuthenticated';
import { useSettledQueryState } from '../useSettledQueryState';

interface UseCurrentUserBaseReturn {
  clearUserProfile: () => void;
  isFetching: boolean;
  isLoading: boolean;
  refetchUser: () => void;
  userProfileError?: SerializedError;
}

/** Returned when `isPatientMasterInfoRequired` is `false` */
interface UseCurrentUserBasicProfileReturn extends UseCurrentUserBaseReturn {
  hasNoPhoneNumber: false;
  userProfile?: ProfileBasic;
}

interface UseCurrentUserParams<T extends boolean = false> {
  getProfileBasicPayload?: GetProfileBasicPayload;
  getProfilePatientMasterPayload?: GetProfilePatientMasterPayload;
  inSequenceCall?: boolean;
  isPatientMasterInfoRequired?: T;
  secure?: boolean;
  skip?: boolean;
}

/** Returned when `isPatientMasterInfoRequired` is `true` */
interface UseCurrentUserPatientMasterProfileReturn extends UseCurrentUserBaseReturn {
  hasNoPhoneNumber: boolean;
  mismatchStatus?: number;
  userProfile?: ProfileFull;
}

type UseCurrentUserReturn<T extends boolean> = T extends true
  ? UseCurrentUserPatientMasterProfileReturn
  : UseCurrentUserBasicProfileReturn;

const useCurrentUserV2 = <T extends boolean = false>({
  skip = false,
  secure = false,
  isPatientMasterInfoRequired = false as T,
  getProfileBasicPayload,
  getProfilePatientMasterPayload,
  inSequenceCall = false,
}: UseCurrentUserParams<T> = {}): UseCurrentUserReturn<T> => {
  const { isAuthenticated } = useIsAuthenticated();
  const dispatch = useAppDispatch();

  const profileBasicQuery = useGetProfileBasicQuery(
    {
      secure,
      ...getProfileBasicPayload,
    },
    {
      skip: !isAuthenticated || skip,
    }
  );
  const { data: profileBasic, error: profileBasicError } = profileBasicQuery;

  const profilePatientMasterQuery = useGetProfilePatientMasterQuery(
    {
      secure,
      ...getProfilePatientMasterPayload,
    },
    {
      skip: !isAuthenticated || skip || !isPatientMasterInfoRequired || (inSequenceCall ? !profileBasic : false),
    }
  );
  const { data: profilePatientMaster, error: profilePatientMasterError } = profilePatientMasterQuery;

  const {
    isFetching: isFetchingBasic,
    isLoading: isLoadingBasic,
    retry: retryBasic,
  } = useSettledQueryState(profileBasicQuery);
  const {
    isFetching: isFetchingPatientMaster,
    isLoading: isLoadingPatientMaster,
    retry: retryPatientMaster,
  } = useSettledQueryState(profilePatientMasterQuery);

  const memberPrn = getProfileBasicPayload?.MemberPrn;
  const { data: ngemrPatient, refetch: refetchNgemrPatient } = useGetProfileEligibilityNgemrPatientQuery(
    {
      MemberPrn: memberPrn,
    },
    {
      skip: !isAuthenticated || skip,
    }
  );
  const isNgemrPatient = ngemrPatient?.IsNGEMRPatient;

  const currentUserReturn = useMemo(() => {
    if (!isPatientMasterInfoRequired) {
      return {
        userProfile: profileBasic?.Profile ? { ...profileBasic.Profile, IsNGEMRPatient: isNgemrPatient } : undefined,
        userProfileError: profileBasicError,
        isFetching: isFetchingBasic,
        isLoading: isLoadingBasic,
        refetchUser: retryBasic,
        hasNoPhoneNumber: false,
        clearUserProfile: () => {
          dispatch(userProfileApi.util.invalidateTags([PROFILE_BASIC_TAGS]));
        },
      };
    }

    return {
      userProfile: { ...profileBasic?.Profile, ...profilePatientMaster?.Profile, IsNGEMRPatient: isNgemrPatient },
      userProfileError: profileBasicError ?? profilePatientMasterError,
      isFetching: isFetchingBasic || isFetchingPatientMaster,
      isLoading: isLoadingBasic || isLoadingPatientMaster,
      refetchUser: () => {
        retryBasic();
        retryPatientMaster();
        refetchNgemrPatient();
      },
      hasNoPhoneNumber: !profilePatientMaster?.Profile?.ContactNumber,
      mismatchStatus: profilePatientMaster?.MismatchStatus,
      clearUserProfile: () => {
        dispatch(userProfileApi.util.invalidateTags([PROFILE_BASIC_TAGS, PROFILE_PATIENT_MASTER_TAGS]));
      },
    };
  }, [
    dispatch,
    isPatientMasterInfoRequired,
    profileBasic?.Profile,
    profilePatientMaster,
    profileBasicError,
    profilePatientMasterError,
    isFetchingBasic,
    isFetchingPatientMaster,
    isLoadingBasic,
    isLoadingPatientMaster,
    isNgemrPatient,
    refetchNgemrPatient,
    retryBasic,
    retryPatientMaster,
  ]);

  return currentUserReturn as UseCurrentUserReturn<T>;
};

export { useCurrentUserV2 };
